import { useEffect, useMemo, useRef, useState } from 'react'
import { API_BASE_URL } from '../../../lib/config'
import { AdminPanelButton } from './AdminPanelButton'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Maximize2, MessageSquareText } from 'lucide-react'

type PromptItem = {
  key: string
  value: string
  defaultValue: string
  limits: { min: number; max: number }
  updatedAt?: string | null
  latestVersion?: number
  activeVersionId?: string | null
  activeVersion?: number | null
  activeVersionName?: string | null
}

type PromptGroups = Record<string, PromptItem[]>

type PromptVersionRow = {
  id: string
  version: number
  versionName: string
  createdAt?: string | null
  value: string
  isActive?: boolean
}

const GROUP_LABELS: Record<string, string> = {
  system: 'System prompts',
  auxiliary: 'Auxiliary prompts',
}

export function PromptConfigurationsSection() {
  const [groups, setGroups] = useState<PromptGroups>({})
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [activeLine, setActiveLine] = useState(1)
  const [versions, setVersions] = useState<Record<string, PromptVersionRow[]>>({})
  const [selectedVersionId, setSelectedVersionId] = useState<Record<string, string | null>>({})
  const [versionNameOpen, setVersionNameOpen] = useState(false)
  const [versionNameDraft, setVersionNameDraft] = useState('')
  const [pendingSaveKey, setPendingSaveKey] = useState<string | null>(null)

  const fetchPrompts = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await fetch(`${API_BASE_URL}admin/configurations/prompts`, {
        method: 'GET',
        credentials: 'include',
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data.error || data.detail || 'Failed to load prompt configurations')
        return
      }
      const loadedGroups = (data.groups ?? {}) as PromptGroups
      setGroups(loadedGroups)
      const nextDrafts: Record<string, string> = {}
      Object.values(loadedGroups).forEach((items) => {
        items.forEach((item) => {
          nextDrafts[item.key] = item.value
        })
      })
      setDrafts(nextDrafts)
    } catch {
      setError('Network error while loading prompt configurations')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPrompts()
  }, [])

  const promptItems = useMemo(() => {
    return Object.entries(groups)
      .map(([groupName, items]) => ({ groupName, items }))
      .filter(({ items }) => Array.isArray(items) && items.length > 0)
  }, [groups])

  const onReset = (item: PromptItem) => {
    setDrafts((prev) => ({ ...prev, [item.key]: item.defaultValue }))
  }

  const fetchVersionsForKey = async (promptKey: string) => {
    setError('')
    try {
      const res = await fetch(`${API_BASE_URL}admin/configurations/prompts/${promptKey}/versions`, {
        method: 'GET',
        credentials: 'include',
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data.error || data.detail || 'Failed to load prompt versions')
        return
      }
      const rows = (data.versions ?? []) as PromptVersionRow[]
      setVersions((prev) => ({ ...prev, [promptKey]: rows }))
    } catch {
      setError('Network error while loading prompt versions')
    }
  }

  const setActiveVersion = async (promptKey: string, promptConfigVersionId: string) => {
    setError('')
    try {
      const res = await fetch(`${API_BASE_URL}admin/configurations/prompts/${promptKey}/active`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promptConfigVersionId }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data.error || data.detail || 'Failed to set active prompt version')
        return
      }
      const loadedGroups = (data.groups ?? {}) as PromptGroups
      setGroups(loadedGroups)
      // Keep drafts aligned with the now-active version
      const nextDrafts: Record<string, string> = {}
      Object.values(loadedGroups).forEach((items) => {
        items.forEach((item) => {
          nextDrafts[item.key] = item.value
        })
      })
      setDrafts((prev) => ({ ...prev, ...nextDrafts }))
      void fetchVersionsForKey(promptKey)
    } catch {
      setError('Network error while setting active prompt version')
    }
  }

  const onSavePrompt = async (promptKey: string, versionName: string) => {
    setError('')
    setSuccess(false)
    const targetValue = drafts[promptKey] ?? ''

    setSaving(true)
    try {
      const res = await fetch(`${API_BASE_URL}admin/configurations/prompts`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompts: {
            [promptKey]: {
              value: targetValue,
              versionName,
            },
          },
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const details = Array.isArray(data.details) ? `: ${data.details.join(', ')}` : ''
        setError((data.error || data.detail || 'Failed to save prompts') + details)
        return
      }
      setSuccess(true)
      await fetchPrompts()
      await fetchVersionsForKey(promptKey)
      setTimeout(() => setSuccess(false), 3000)
    } catch {
      setError('Network error while saving prompt configurations')
    } finally {
      setSaving(false)
    }
  }

  const activeItem = useMemo(() => {
    if (!editingKey) return null
    for (const { items } of promptItems) {
      const found = items.find((item) => item.key === editingKey)
      if (found) return found
    }
    return null
  }, [editingKey, promptItems])

  const activeValue = activeItem ? (drafts[activeItem.key] ?? '') : ''
  const lineCount = Math.max(1, activeValue.split('\n').length)

const activeHasChanges = useMemo(() => {
    if (!activeItem) return false
    return (drafts[activeItem.key] ?? '') !== activeItem.value
  }, [activeItem, drafts])

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const lineNumRef = useRef<HTMLDivElement>(null)
  const shadowRef = useRef<HTMLDivElement>(null)
  const [editorWidth, setEditorWidth] = useState(0)
  const [visualLineEntries, setVisualLineEntries] = useState<(number | null)[]>([])

  useEffect(() => {
    if (!activeItem) return
    void fetchVersionsForKey(activeItem.key)
    const activeId = activeItem.activeVersionId ?? null
    setSelectedVersionId((prev) => ({ ...prev, [activeItem.key]: activeId }))
  }, [activeItem])

  // Track textarea width via ResizeObserver
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setEditorWidth(el.clientWidth))
    ro.observe(el)
    setEditorWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [activeItem])

  // Use a shadow div to let the browser measure actual wrap row counts
  useEffect(() => {
    const shadow = shadowRef.current
    const lines = activeValue.split('\n')
    if (!shadow || !editorWidth) {
      setVisualLineEntries(lines.map((_, i) => i + 1))
      return
    }
    const lineHeightPx = 24
    const entries: (number | null)[] = []
    lines.forEach((line, i) => {
      shadow.textContent = line || '\u200b'
      const rows = Math.max(1, Math.round(shadow.scrollHeight / lineHeightPx))
      entries.push(i + 1)
      for (let j = 1; j < rows; j++) entries.push(null)
    })
    setVisualLineEntries(entries)
  }, [activeValue, editorWidth])

  const syncScroll = () => {
    if (textareaRef.current && lineNumRef.current) {
      lineNumRef.current.scrollTop = textareaRef.current.scrollTop
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading prompt configurations...</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquareText className="h-5 w-5" />
          Prompt editor
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Select a prompt to edit in fullscreen mode. Only system and auxiliary prompts are configurable.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {promptItems.map(({ groupName, items }) => (
            <div key={groupName} className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {GROUP_LABELS[groupName] || groupName}
              </h3>
              {items.map((item) => {
                const value = drafts[item.key] ?? ''
                const charCount = value.length
                const tooShort = charCount < item.limits.min
                const tooLong = charCount > item.limits.max
                const changed = value !== item.value
                return (
                  <div key={item.key} className="rounded-md border px-3 py-2">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-mono text-[12px] sm:text-[13px]">{item.key}</p>
                          {changed && (
                            <span className="text-[11px] rounded-full border px-2 py-0.5 bg-amber-100 text-amber-900 border-amber-300">
                              Unsaved
                            </span>
                          )}
                          {typeof item.activeVersion === 'number' && (
                            <span
                              className="text-[11px] rounded-full border px-2 py-0.5 bg-emerald-50 text-emerald-900 border-emerald-300"
                              title="This version is what the app loads at runtime"
                            >
                              In use: {item.activeVersionName ? `${item.activeVersionName} · ` : ''}v
                              {item.activeVersion}
                            </span>
                          )}
                          {typeof item.latestVersion === 'number' &&
                            typeof item.activeVersion === 'number' &&
                            item.latestVersion !== item.activeVersion && (
                              <span className="text-[11px] rounded-full border px-2 py-0.5 text-muted-foreground">
                                Latest saved v{item.latestVersion}
                              </span>
                            )}
                          {typeof item.latestVersion === 'number' &&
                            typeof item.activeVersion !== 'number' && (
                              <span className="text-[11px] rounded-full border px-2 py-0.5 text-muted-foreground">
                                v{item.latestVersion}
                              </span>
                            )}
                        </div>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {charCount} chars
                          {item.updatedAt ? ` · ${new Date(item.updatedAt).toLocaleString()}` : ''}
                        </p>
                        {(tooShort || tooLong) && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            {tooShort
                              ? `Minimum ${item.limits.min} characters required.`
                              : `Maximum ${item.limits.max} characters exceeded.`}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <AdminPanelButton type="button" size="sm" onClick={() => setEditingKey(item.key)}>
                          <Maximize2 className="h-4 w-4" />
                          Edit
                        </AdminPanelButton>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ))}

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          {success && <p className="text-sm text-green-600 dark:text-green-400">Saved prompt updates.</p>}
        </div>
      </CardContent>
      {activeItem && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,30,56,0.52)',
            backdropFilter: 'blur(6px)',
            zIndex: 500,
            display: 'flex',
            animation: 'bIn .3s ease both',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'var(--white)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '18px 26px 14px',
                borderBottom: '1px solid var(--bd2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
                background: 'var(--white)',
              }}
            >
              <div>
                <h3
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 17,
                    fontWeight: 500,
                    color: 'var(--navy)',
                    fontStyle: 'italic',
                  }}
                >
                  {activeItem.key}
                </h3>
                <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>
                  In use (app runtime): {activeItem.activeVersionName ? `${activeItem.activeVersionName} · ` : ''}
                  {typeof activeItem.activeVersion === 'number' ? `v${activeItem.activeVersion}` : `v${activeItem.latestVersion ?? 1}`}{' '}
                  · {activeValue.length} chars · line {activeLine} of {lineCount}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 10.5, color: 'var(--muted2)' }}>Version (in use marked)</span>
                  <select
                    value={selectedVersionId[activeItem.key] ?? activeItem.activeVersionId ?? ''}
                    onChange={(e) => {
                      const nextId = e.target.value || null
                      setSelectedVersionId((prev) => ({ ...prev, [activeItem.key]: nextId }))
                      const rows = versions[activeItem.key] ?? []
                      const row = rows.find((r) => r.id === nextId)
                      if (row) {
                        setDrafts((prev) => ({ ...prev, [activeItem.key]: row.value }))
                        setActiveLine(1)
                      }
                    }}
                    style={{
                      height: 32,
                      padding: '0 10px',
                      borderRadius: 9,
                      border: '1px solid var(--bd)',
                      background: 'var(--white)',
                      color: 'var(--text)',
                      fontSize: 12.5,
                      cursor: 'pointer',
                      minWidth: 220,
                      outline: 'none',
                    }}
                  >
                    {(versions[activeItem.key] ?? []).map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.versionName} · v{v.version}
                        {v.isActive ? ' — in use' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <AdminPanelButton
                  type="button"
                  size="sm"
                  disabled={
                    saving ||
                    !(selectedVersionId[activeItem.key] && selectedVersionId[activeItem.key] !== activeItem.activeVersionId)
                  }
                  onClick={() => {
                    const targetId = selectedVersionId[activeItem.key]
                    if (targetId) void setActiveVersion(activeItem.key, targetId)
                  }}
                >
                  Set active
                </AdminPanelButton>
                <AdminPanelButton
                  type="button"
                  size="icon"
                  onClick={() => setEditingKey(null)}
                  aria-label="Close editor"
                >
                  ✕
                </AdminPanelButton>
              </div>
            </div>

            {/* Shadow div — mirrors textarea styling so the browser can measure
                actual wrap row counts; kept off-screen and invisible */}
            <div
              ref={shadowRef}
              aria-hidden
              style={{
                position: 'fixed',
                top: 0,
                left: -9999,
                visibility: 'hidden',
                pointerEvents: 'none',
                fontFamily: 'monospace',
                fontSize: 13,
                lineHeight: '24px',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                width: Math.max(0, editorWidth - 32),
                overflow: 'hidden',
              }}
            />

            {/* Editor */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                overflow: 'hidden',
                background: 'var(--white)',
              }}
            >
              {/* Line numbers */}
              <div
                ref={lineNumRef}
                style={{
                  width: 56,
                  borderRight: '1px solid var(--bd2)',
                  background: 'var(--bg)',
                  textAlign: 'right',
                  fontSize: 12,
                  lineHeight: '24px',
                  overflow: 'hidden',
                  padding: '16px 10px',
                  fontFamily: 'monospace',
                  userSelect: 'none',
                  flexShrink: 0,
                }}
              >
                {visualLineEntries.map((n, idx) => (
                  <div
                    key={idx}
                    style={{
                      color: n === activeLine ? 'var(--navy)' : 'var(--muted2)',
                      fontWeight: n === activeLine ? 600 : 400,
                    }}
                  >
                    {n ?? '.'}
                  </div>
                ))}
              </div>

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                style={{
                  flex: 1,
                  resize: 'none',
                  padding: '16px',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  lineHeight: '24px',
                  outline: 'none',
                  background: 'var(--white)',
                  color: 'var(--text)',
                  overflowY: 'auto',
                  border: 'none',
                }}
                wrap="soft"
                value={activeValue}
                onScroll={syncScroll}
                onChange={(evt) => {
                  const next = evt.target.value
                  setDrafts((prev) => ({ ...prev, [activeItem.key]: next }))
                  const caret = evt.target.selectionStart
                  const line = next.slice(0, caret).split('\n').length
                  setActiveLine(line)
                }}
                onClick={(evt) => {
                  const val = evt.currentTarget.value
                  const line = val.slice(0, evt.currentTarget.selectionStart).split('\n').length
                  setActiveLine(line)
                }}
                onKeyUp={(evt) => {
                  const val = evt.currentTarget.value
                  const line = val.slice(0, evt.currentTarget.selectionStart).split('\n').length
                  setActiveLine(line)
                }}
              />
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '14px 26px 18px',
                borderTop: '1px solid var(--bd2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
                background: 'var(--white)',
              }}
            >
              <p style={{ fontSize: 11.5, color: 'var(--muted2)' }}>
                Limits: {activeItem.limits.min}–{activeItem.limits.max} chars
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <AdminPanelButton type="button" size="sm" onClick={() => onReset(activeItem)}>
                  Reset default
                </AdminPanelButton>
                <AdminPanelButton type="button" size="sm" onClick={() => setEditingKey(null)}>
                  Close
                </AdminPanelButton>
                <AdminPanelButton
                  type="button"
                  size="sm"
                  variant="active"
                  onClick={() => {
                    setPendingSaveKey(activeItem.key)
                    setVersionNameDraft('')
                    setVersionNameOpen(true)
                  }}
                  disabled={saving || !activeHasChanges}
                >
                  {saving ? 'Saving…' : 'Save prompt'}
                </AdminPanelButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version name modal */}
      {versionNameOpen && pendingSaveKey && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,30,56,0.52)',
            backdropFilter: 'blur(6px)',
            zIndex: 650,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'bIn .3s ease both',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) setVersionNameOpen(false)
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--white)',
              borderRadius: 20,
              width: 420,
              maxWidth: '94vw',
              boxShadow: 'var(--sh-lg)',
              overflow: 'hidden',
              animation: 'mIn .4s cubic-bezier(.16,1,.3,1) both',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                padding: '20px 26px 16px',
                borderBottom: '1px solid var(--bd2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}
            >
              <div>
                <h3
                  style={{
                    fontFamily: "'Playfair Display',serif",
                    fontSize: 17,
                    fontWeight: 500,
                    color: 'var(--navy)',
                  }}
                >
                  Name this version
                </h3>
                <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
                  This label will help you identify which version is active.
                </p>
              </div>
              <AdminPanelButton
                type="button"
                size="icon"
                disabled={saving}
                onClick={() => setVersionNameOpen(false)}
                aria-label="Close"
              >
                ✕
              </AdminPanelButton>
            </div>
            <div style={{ padding: '20px 26px' }}>
              <input
                type="text"
                value={versionNameDraft}
                onChange={(e) => setVersionNameDraft(e.target.value)}
                placeholder="e.g. Experiment A"
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 14,
                  color: 'var(--text)',
                  background: 'var(--bg2)',
                  border: '1px solid var(--bd)',
                  borderRadius: 9,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div
              style={{
                padding: '14px 26px 20px',
                borderTop: '1px solid var(--bd2)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <AdminPanelButton
                type="button"
                size="sm"
                onClick={() => setVersionNameOpen(false)}
                disabled={saving}
              >
                Cancel
              </AdminPanelButton>
              <AdminPanelButton
                type="button"
                size="sm"
                variant="primary"
                disabled={saving || !versionNameDraft.trim()}
                onClick={async () => {
                  const name = versionNameDraft.trim()
                  if (!name) return
                  await onSavePrompt(pendingSaveKey, name)
                  setVersionNameOpen(false)
                  setPendingSaveKey(null)
                }}
              >
                {saving ? 'Saving…' : 'Save'}
              </AdminPanelButton>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}
